// Royal Mail Click & Drop CSV export.
// Column headers match the "Import your orders" template that Click & Drop
// accepts out-of-the-box (Manage > Orders > Import > CSV).
// Reference: https://www.royalmail.com/business/shipping/click-drop/importing-orders

import { findShipping } from "./shipping";

export type ClickDropOrder = {
  id: string;
  amount_pence: number;
  shipping_method: string | null;
  tracking_number: string | null;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  // Stripe checkout session details persisted onto the order.
  shipping_address: {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: {
      line1?: string | null;
      line2?: string | null;
      city?: string | null;
      state?: string | null;
      postal_code?: string | null;
      country?: string | null;
    } | null;
  } | null;
  created_at: string;
  auctions: {
    id: string;
    title: string;
    weight_grams: number | null;
  } | null;
};

// Map our internal shipping IDs to Royal Mail Click & Drop service codes.
// (Sellers can edit the mapping inside Click & Drop before purchasing labels.)
const SERVICE_CODE: Record<string, string> = {
  royal_mail_tracked_48: "TPN",       // Tracked 48
  royal_mail_tracked_24: "TPS",       // Tracked 24
  royal_mail_special: "SD1",          // Special Delivery Guaranteed by 1pm
  international_tracked: "TRM24",     // International Tracked
};

// Neutralize CSV formula injection (Excel / Sheets / LibreOffice). Any value
// starting with =, +, -, @, TAB or CR is prefixed with a single apostrophe and
// quoted so the apostrophe is stored literally. Click & Drop imports the value
// as-is; addresses/names/refs remain human-readable.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  let s = String(v);
  const needsFormulaGuard = FORMULA_TRIGGER.test(s);
  if (needsFormulaGuard) s = `'${s}`;
  if (needsFormulaGuard || /[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

const HEADERS = [
  "Order reference",
  "Order date",
  "Customer name",
  "Email address",
  "Mobile number",
  "Address line 1",
  "Address line 2",
  "City",
  "County",
  "Postcode",
  "Country",
  "Order value",
  "Currency",
  "Weight (grams)",
  "Postage service",
  "Service code",
  "SKU",
  "Description",
  "Quantity",
];

export function buildClickDropCsv(orders: ClickDropOrder[]): string {
  const rows: string[] = [HEADERS.map(csvCell).join(",")];
  for (const o of orders) {
    const addr = o.shipping_address?.address ?? {};
    const name = o.buyer_name ?? o.shipping_address?.name ?? "";
    const email = o.buyer_email ?? o.shipping_address?.email ?? "";
    const phone = o.buyer_phone ?? o.shipping_address?.phone ?? "";
    const ship = findShipping(o.shipping_method);
    const serviceLabel = ship?.label ?? o.shipping_method ?? "";
    const serviceCode = o.shipping_method ? SERVICE_CODE[o.shipping_method] ?? "" : "";
    const ref = o.id.slice(0, 8).toUpperCase();
    rows.push([
      ref,
      new Date(o.created_at).toISOString().slice(0, 10),
      name,
      email,
      phone,
      addr.line1 ?? "",
      addr.line2 ?? "",
      addr.city ?? "",
      addr.state ?? "",
      addr.postal_code ?? "",
      addr.country ?? "GB",
      (o.amount_pence / 100).toFixed(2),
      "GBP",
      o.auctions?.weight_grams ?? 100,
      serviceLabel,
      serviceCode,
      o.auctions?.id?.slice(0, 8) ?? "",
      o.auctions?.title ?? "Trading card",
      1,
    ].map(csvCell).join(","));
  }
  return rows.join("\r\n") + "\r\n";
}

export function downloadClickDropCsv(orders: ClickDropOrder[]) {
  const csv = buildClickDropCsv(orders);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = orders.length === 1
    ? `click-drop-${orders[0].id.slice(0, 8)}-${stamp}.csv`
    : `click-drop-${orders.length}-orders-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
